import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, MapPin, Instagram, Star, Plus, ShoppingBag, ChevronDown, Clock, Truck, User, X, Search, Gift, Info, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ItemModal from '../components/ItemModal';
import Footer from '../components/Footer';
import { api } from '../services/api';
import { isStoreOpen, getNextOpenTime } from '../utils/storeHours';

const slugify = value => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');


export default function MenuPage() {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const {
  cart,
  cartCount,
  cartSubtotal,
  orderType,
  setOrderType,
  user,
  updateQuantity,
  addToCart,
  activePromotions,
  selectedPromotion,
  setSelectedPromotion
} = useApp();
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(!orderType);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [storeOpen, setStoreOpen] = useState(true);
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [closedMessage, setClosedMessage] = useState('');
  const [nextOpenTime, setNextOpenTime] = useState(null);
  const [closedModalDismissed, setClosedModalDismissed] = useState(false);

  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [promotionInfoModal, setPromotionInfoModal] = useState(null);
  // Data state
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasActiveBogo, setHasActiveBogo] = useState(false);

  const categoryRefs = useRef({});
  const categoryNavRef = useRef(null);
  const scrollingRef = useRef(false);
  const availabilityRef = useRef({ open: true, paused: false });

  const selectedCategory = categorySlug
    ? categories.find(category => slugify(category.name) === categorySlug)
    : null;
  const visibleCategories = categories.filter(category => {
    if (category.name === 'Special Offers' && !hasActiveBogo) return false;
    return !categorySlug || category.id === selectedCategory?.id;
  });

  const nextOpeningLabel = useCallback((rest) => {
    const value = rest?.tradingStatus?.nextOpenAt;
    if (!value) return getNextOpenTime(rest?.openingHours, rest?.specialHours);
    const next = new Date(value);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateKey = date => date.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    const day = dateKey(next) === dateKey(now)
      ? 'today'
      : dateKey(next) === dateKey(tomorrow)
        ? 'tomorrow'
        : next.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'Europe/London' });
    return {
      day,
      time: next.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London'
      })
    };
  }, []);

  const applyOrderingAvailability = useCallback((rest) => {
    const paused = Boolean(rest?.pauseIncomingOrders);
    const resolvedOpen = rest?.tradingStatus?.isOpen;
    const open = typeof resolvedOpen === 'boolean'
      ? resolvedOpen
      : !paused && isStoreOpen(rest?.openingHours, rest?.specialHours);
    const previous = availabilityRef.current;

    setOrdersPaused(paused);
    setStoreOpen(open);
    setClosedMessage(rest?.tradingStatus?.message || 'Our store is not accepting orders right now, but you can still browse our menu!');
    setNextOpenTime(!open && !paused ? nextOpeningLabel(rest) : null);
    if (!open) {
      setShowOrderTypeModal(false);
      if (previous.open || previous.paused !== paused) {
        setClosedModalDismissed(false);
      }
    }
    availabilityRef.current = { open, paused };
  }, [nextOpeningLabel]);

  const getFilteredItems = (catId) => {
    const items = menuItems.filter(i => i.categoryId === catId && i.available);

    if (!searchQuery.trim()) return items;

    const q = searchQuery.toLowerCase();

    return items.filter(i =>
      i.name.toLowerCase().includes(q) ||
      (i.description && i.description.toLowerCase().includes(q))
    );
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rest, cats, items, reviewStats, bogoStatus] = await Promise.all([
          api.getRestaurant(),
          api.getCategories(),
          api.getMenuItems(),
          api.getReviewsStats(),
          api.checkActiveBogo()
        ]);
        setRestaurant({ ...rest, reviewStats });
        setCategories(cats);
        setMenuItems(items);
        setHasActiveBogo(bogoStatus.hasActiveBogo);
        if (categorySlug) {
          const found = cats.find(category => slugify(category.name) === categorySlug);
          if (!found) {
            navigate('/', { replace: true });
            return;
          }
          setActiveCategory(found.id);
        } else if (cats.length > 0) {
          setActiveCategory(cats[0].id);
        }
        
        applyOrderingAvailability(rest);
      } catch (err) {
        console.error('Failed to load menu data:', err);
      } finally {
        window.__SEO_READY__ = true;
        setLoading(false);
      }
    };
    fetchData();
  }, [applyOrderingAvailability, categorySlug, navigate]);

  // Keep already-open storefronts in sync when staff pause or resume orders.
  useEffect(() => {
    let active = true;
    const refreshAvailability = async () => {
      try {
        const rest = await api.getRestaurant();
        if (!active) return;
        setRestaurant(current => ({ ...current, ...rest }));
        applyOrderingAvailability(rest);
      } catch (_) {
        // Retain the last known availability during a temporary network error.
      }
    };
    const intervalId = window.setInterval(refreshAvailability, 15000);
    window.addEventListener('focus', refreshAvailability);
    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshAvailability);
    };
  }, [applyOrderingAvailability]);

  useEffect(() => {
  if (isSearching) window.scrollTo({ top: 0 });
}, [isSearching]);

const hasSearchResults = menuItems.some(i => {
  const q = searchQuery.toLowerCase();
  return (
    i.available &&
    (!categorySlug || i.categoryId === selectedCategory?.id) &&
    (
      i.name.toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q)
    )
  );
});

  const handleOrderTypeSelect = (type) => {
    setOrderType(type);
    setShowOrderTypeModal(false);
  };

  useEffect(() => {
  if (categorySlug && selectedCategory && restaurant?.name) {
    document.title = `${selectedCategory.name} - ${restaurant.name}`;
  } else if (restaurant?.seoTitle) {
    document.title = restaurant.seoTitle;
  } else if (restaurant?.name) {
    document.title = restaurant.name;
  } else {
    document.title = "Restaurant";
  }
}, [restaurant, categorySlug, selectedCategory]);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticky(window.scrollY > 180);
      if (categorySlug) return;
      if (scrollingRef.current) return;
      const offsets = categories.map(cat => {
        const el = categoryRefs.current[cat.id];
        if (!el) return { id: cat.id, top: Infinity };
        return { id: cat.id, top: el.getBoundingClientRect().top };
      });
      const visible = offsets.filter(o => o.top <= 140);
      if (visible.length > 0) setActiveCategory(visible[visible.length - 1].id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories, categorySlug]);

  const scrollToCategory = useCallback((catId) => {
    const el = categoryRefs.current[catId];
    if (!el) return;
    scrollingRef.current = true;
    setActiveCategory(catId);
    const offset = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top: offset, behavior: 'smooth' });
    setTimeout(() => { scrollingRef.current = false; }, 800);
  }, []);

  useEffect(() => {
    if (categorySlug) return;
    const nav = categoryNavRef.current;
    if (!nav) return;
    const activeBtn = nav.querySelector(`[data-catid="${activeCategory}"]`);
    if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeCategory, categorySlug]);

  const getCategoryItems = (catId) => menuItems.filter(i => i.categoryId === catId && i.available);
  const CHECKOUT_STORAGE_KEY = 'checkoutDeliveryState';

  let deliveryFee = 0;

  if (orderType === 'delivery') {
    deliveryFee = restaurant?.deliveryFee || 2.50;

    try {
      const saved = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.deliveryFeeInfo?.fee !== undefined) {
          deliveryFee = parsed.deliveryFeeInfo.fee;
        }
      }
    } catch (e) {}
  }
  const total = cartSubtotal + deliveryFee;

  useEffect(() => {
  const CHECKOUT_STORAGE_KEY = 'checkoutDeliveryState';

  const syncCheckoutState = () => {
    try {
      const saved = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved);

      if (parsed?.activeTab && parsed.activeTab !== orderType) {
        setOrderType(parsed.activeTab);
      }
    } catch (e) {
      console.error('Failed syncing checkout state on menu page:', e);
    }
  };

  syncCheckoutState();
  window.addEventListener('focus', syncCheckoutState);

  return () => {
    window.removeEventListener('focus', syncCheckoutState);
  };
}, [orderType, setOrderType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-amber-600 border-t-transparent rounded-full animate-spin" style={{ borderWidth: 3 }} />
          <p className="text-gray-500 text-sm">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (

    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Store Closed Modal */}
      {!storeOpen && !closedModalDismissed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {ordersPaused ? 'Check back soon' : "We're Currently Closed"}
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {ordersPaused
                  ? 'We are not accepting orders currently, please check back soon.'
                  : closedMessage}
              </p>
              {nextOpenTime && (
                <p className="text-amber-700 text-sm font-semibold mb-6">
                  We open {nextOpenTime.day} at {nextOpenTime.time}
                </p>
              )}
              <button
                onClick={() => setClosedModalDismissed(true)}
                className="w-full py-3 px-6 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-all duration-200"
              >
                Browse Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Type Modal */}
      {showOrderTypeModal && storeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 animate-fade-in">
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-6">How would you like to order?</h2>
            <div className="flex gap-3">
              <button
                onClick={() => handleOrderTypeSelect('pickup')}
                className="flex-1 py-3 px-6 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:border-amber-500 hover:text-amber-700 transition-all duration-200"
              >
                Pickup
              </button>
              <button
                onClick={() => handleOrderTypeSelect('delivery')}
                className="flex-1 py-3 px-6 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-all duration-200"
              >
                Delivery
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Header Bar */}
      <div className="bg-amber-700 h-14 flex items-center px-6 justify-between">
        <div className="w-10"></div>
        <div className="flex items-center gap-3 ml-auto">

          {/* Rewards Button */}
            {restaurant?.rewardsEnabled && (
              <button
                onClick={() => navigate("/rewards")}
                className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm transition-colors"
              >
                <Gift className="w-4 h-4" />
                <span>Rewards</span>
              </button>
            )}

            {/* Account */}
            <button
              onClick={() => navigate(user ? '/account' : '/login')}
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm transition-colors"
            >
              <User className="w-4 h-4" />
              <span>
                {user ? user.name?.split(' ')[0] : 'Account'}
              </span>
            </button>

          {user?.isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg transition-colors font-semibold"
            >
              Admin
            </button>
          )}
        </div>
      </div>

      {/* Restaurant Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-6 flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{restaurant?.name}</h1>
            <div className="flex items-center gap-2 mb-2">
              {!storeOpen && !ordersPaused && nextOpenTime ? (
                <span className="text-red-600 font-medium text-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                  Closed · Opens {nextOpenTime.day === 'tomorrow' ? 'tomorrow' : nextOpenTime.day === 'today' ? 'later today' : `on ${nextOpenTime.day}`} at {nextOpenTime.time}
                </span>
              ) : storeOpen ? (
                (() => {
                  const hours = restaurant?.tradingStatus;
                  if (hours && !hours.closed) {
                    return <span className="text-sm font-medium flex items-center gap-1.5 text-gray-700"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>Open today · {hours.open} – {hours.close}</span>;
                  }
                  return <span className="text-sm font-medium flex items-center gap-1.5 text-gray-700"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>Open now</span>;
                })()
              ) : (
                <span className="text-red-600 font-medium text-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                  {ordersPaused ? 'Not accepting orders' : 'Closed'}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mb-3">{restaurant?.description}</p>
            <div className="flex flex-col gap-1.5 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{restaurant?.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-gray-400" />
                <span>@{restaurant?.instagram}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{restaurant?.address}</span>
              </div>
            </div>
          </div>
<div className="lg:w-96 flex flex-col gap-3">

  {/* Mobile Carousel */}
  <div className="overflow-x-auto no-scrollbar lg:hidden">
    <div className="flex gap-4 px-1 snap-x snap-mandatory scroll-smooth">

      {/* Main Restaurant Banner */}
      <div
        className="
          relative
          w-[320px]
          min-w-[320px]
          max-w-[320px]
          h-44
          rounded-2xl
          overflow-hidden
          shadow-sm
          snap-center
          flex-shrink-0
          bg-gray-200
        "
      >
        {restaurant?.banner && (
          <img
            src={restaurant.banner}
            alt="Restaurant"
            className="w-full h-full object-cover"
          />
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-center py-2 text-sm font-semibold tracking-wide">
          {restaurant?.bannerText || 'Special Offers Available'}
        </div>
      </div>

      {/* Promotions */}
      {(activePromotions || []).map((promo) => (
        <div
          key={promo.id}
          className="
            relative
            w-[320px]
            min-w-[320px]
            max-w-[320px]
            h-44
            rounded-2xl
            overflow-hidden
            shadow-sm
            snap-center
            flex-shrink-0
            bg-gray-200
          "
        >
          {promo.bannerImage ? (
            <img
              src={promo.bannerImage}
              alt={promo.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-amber-500" />
          )}

          <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPromotionInfoModal(promo);
            }}
            className="
              absolute
              top-3
              right-3
              z-10
              w-7
              h-7
              rounded-full
              bg-white
              flex
              items-center
              justify-center
              text-gray-900
              shadow-sm
            "
          >
            <Info className="w-4 h-4" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 bg-amber-600 text-white text-center py-2 text-sm font-semibold tracking-wide">
            {promo.title}
          </div>
        </>
        </div>
      ))}

    </div>
  </div>

  {/* Desktop Restaurant Banner */}
  <div className="hidden lg:block">
    <div
      className="
        relative
        h-44
        rounded-2xl
        overflow-hidden
        shadow-sm
        bg-gray-200
      "
    >
      {restaurant?.banner && (
        <img
          src={restaurant.banner}
          alt="Restaurant"
          className="w-full h-full object-cover"
        />
      )}

      <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-center py-2 text-sm font-semibold tracking-wide">
        {restaurant?.bannerText || 'Special Offers Available'}
      </div>
    </div>
  </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                <Clock className="w-4 h-4 text-amber-600" />
                <span>
                  {(orderType === 'pickup'
                    ? restaurant?.pickupTime
                    : restaurant?.deliveryTime
                  )
                    ?.replace(/\s*-\s*/g, '-')   // tighten dash
                    ?.replace(/\s*mins?/i, ' mins') // ensure space before mins
                    ?.toLowerCase()}
                </span>
              </div>
                <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                  <Truck className="w-4 h-4 text-amber-600" />
                  <span>Delivery From: £{(restaurant?.deliveryFee || 2.50).toFixed(2)}</span>
                </div>
              {restaurant?.reviewStats?.count > 0 && (
                <button 
                  onClick={() => navigate('/reviews')}
                  className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-1 py-1 transition-colors hover:bg-gray-50 sm:px-2"
                >
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-semibold text-gray-800">{restaurant.reviewStats.avgRating}</span>
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Desktop Promotions */}
{activePromotions?.length > 0 && (
  <div className="hidden lg:flex gap-4 overflow-x-auto no-scrollbar pb-1 mb-5">

    {activePromotions.map((promo) => {

      const qualifies = cartSubtotal >= promo.minSpend;

      return (
        <button
          key={promo.id}
          type="button"
          onClick={() => {
            if (!qualifies) return;
            setSelectedPromotion(promo);
          }}
          className={`
            min-w-[300px]
            rounded-2xl
            border
            px-5
            py-4
            text-left
            transition-all
            duration-200
            ${
              qualifies
                ? 'border-gray-200 bg-white'
                : 'border-gray-200 bg-gray-50 opacity-70'
            }
          `}
        >

          <div className="flex items-start justify-between gap-4">

            <div>
              <p className="text-sm font-semibold text-gray-900">
                {promo.title}
              </p>

              <p className="text-xs text-gray-500 mt-1">
                Minimum Spend £{promo.minSpend.toFixed(2)}
              </p>
            </div>

            <div
              className={`
                px-3
                py-1.5
                rounded-xl
                text-xs
                font-bold
                whitespace-nowrap
                ${
                  qualifies
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }
              `}
            >
              {qualifies ? 'Choose' : 'Locked'}
            </div>

          </div>

        </button>
      );
    })}

  </div>
)}
  </div>

      {/* Category Nav */}
      <div className={`sticky top-0 z-30 bg-white border-b border-gray-200 transition-shadow duration-200 ${isHeaderSticky ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-2 py-2">

{/* SEARCH BAR */}
<div
  className={`
    overflow-hidden transition-all duration-300 ease-in-out
    ${isSearching ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}
  `}
>
  <div
    className={`
      flex items-center w-full gap-2
      transform transition-all duration-300 ease-in-out
      ${isSearching ? 'translate-x-0' : 'translate-x-full'}
    `}
  >
    <div className="relative flex-1">
      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />

      <input
        autoFocus={isSearching}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search the menu"
        className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-base focus:outline-none"
      />
    </div>

    <button
      onClick={() => {
        setIsSearching(false);
        setSearchQuery('');
      }}
      className="text-sm font-medium text-gray-600 whitespace-nowrap"
    >
      Cancel
    </button>
  </div>
</div>

  {/* NO RESULTS MESSAGE */}
  {isSearching && searchQuery.trim() && !hasSearchResults && (
    <div className="text-center py-6 text-gray-400 text-sm font-medium">
      No results found
    </div>
  )}

 {/* CATEGORY ROW */}
{!isSearching && (
  <div className="flex items-center gap-2">

    {categorySlug && (
      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium text-gray-700 rounded-md border border-gray-200 bg-white hover:bg-gray-100"
        aria-label="Back to the full menu"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to menu
      </button>
    )}

    {/* SCROLLABLE CATEGORIES */}
    <div
      ref={categoryNavRef}
      className="flex flex-1 min-w-0 overflow-x-auto scrollbar-hide"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      <div className="flex gap-2 pr-2">
        {visibleCategories.map(cat => (
          <button
            key={cat.id}
            data-catid={cat.id}
            onClick={() => scrollToCategory(cat.id)}
            className={`flex-shrink-0 px-4 py-1 text-sm font-medium whitespace-nowrap rounded-md transition-all duration-200 ${
              activeCategory === cat.id
                ? 'bg-amber-600 text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>
    </div>

    {/* FIXED SEARCH BUTTON (THIS WAS YOUR PROBLEM) */}
    <div className="flex-shrink-0 pl-2 border-l border-gray-200">
      <button
        onClick={() => setIsSearching(true)}
        className="lg:hidden p-2 text-gray-600"
      >
        <Search className="w-5 h-5" />
      </button>
    </div>

  </div>
)}
</div>
      </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            {/* Offer Banner - Only show if BOGO is active */}
            {hasActiveBogo && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
                <p className="text-green-800 font-semibold text-sm">Buy One Get One Half Price Notice...</p>
                <p className="text-green-700 text-xs mt-1">Please ensure you have added two items to validate your offer. The amount will be deducted automatically upon checkout.</p>
              </div>
            )}

            {visibleCategories
              .map(cat => {
              const items = getFilteredItems(cat.id);
              if (items.length === 0) return null;
              return (
                <div key={cat.id} id={`cat_${cat.id}`} ref={el => { categoryRefs.current[cat.id] = el; }} className="mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{cat.name}</h2>
                  <div className="space-y-3">
                    {items.map(item => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        onClick={() => setSelectedItem(item)}
                        rewardsEnabled={restaurant?.rewardsEnabled}
                        pointsPerPound={restaurant?.pointsPerPound || 10}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {categories.length === 0 && !loading && (
              <div className="text-center py-16 text-gray-400">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Menu is being set up</p>
                <p className="text-sm mt-1">Check back soon!</p>
              </div>
            )}
          </div>

          {/* Cart Sidebar Desktop */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <CartSidebar navigate={navigate} deliveryFee={deliveryFee} total={total} storeOpen={storeOpen} ordersPaused={ordersPaused} />
          </div>
        </div>
      </div>

      {/* Mobile Cart Button */}
      {cartCount > 0 && (
        <div className="lg:hidden fixed bottom-6 left-0 right-0 px-4 z-40">
          <button
            onClick={() => setShowMobileCart(true)}
            className="w-full bg-gray-900 text-white py-4 rounded-2xl flex items-center justify-between px-5 shadow-2xl hover:bg-gray-800 transition-colors duration-200"
          >
            <span className="bg-white text-gray-900 rounded-lg w-7 h-7 flex items-center justify-center text-sm font-bold">{cartCount}</span>
            <span className="font-semibold text-base">View basket</span>
            <span className="font-semibold">£{total.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Mobile Cart Modal */}
      {showMobileCart && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileCart(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Your Order ({cartCount})</h3>
              <button onClick={() => setShowMobileCart(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cart.map(item => {
  const isPromoReward = item.promotionReward;

  return (
    <div key={item.cartItemId} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  {isPromoReward ? (
  <div className="px-3 py-2 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs font-bold">
    FREE
  </div>
) : (
  <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200">
    <button onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-gray-600">−</button>
    <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
    <button disabled={!storeOpen} onClick={() => storeOpen && updateQuantity(item.cartItemId, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed">+</button>
  </div>
)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <p className="text-xs text-gray-500 truncate">
                        {Object.entries(item.selectedOptions).map(([optId, choice]) => {
                          if (Array.isArray(choice)) return choice.map(c => c.name).join(', ');
                          return choice?.name || '';
                        }).filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-gray-900">£{(item.price * item.quantity).toFixed(2)}</span>
                </div>
  );
})}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">

              {/* Mobile Upsell Section */}
{menuItems
  .filter(item => item.isUpsell && item.available)
  .slice(0, 8).length > 0 && (
  <div className="pt-4 pr-4 pb-4">
    <h3 className="text-sm font-bold text-gray-900 mb-3">
      Room for more?
    </h3>

    {/* Horizontal scroll carousel */}
    <div className="overflow-x-auto scrollbar-hide">
      <div className="flex gap-3 pr-4">
        {menuItems
          .filter(item => item.isUpsell && item.available)
          .slice(0, 8)
          .map(item => (
            <div
              key={item.id}
              className="min-w-[85%] flex items-center gap-3 bg-white border border-gray-200 rounded-2xl p-3 flex-shrink-0"
            >
              {/* LEFT IMAGE — only show if real image exists */}
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-gray-100"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : null}

              {/* CENTER INFO */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {item.name}
                </p>

                {item.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                    {item.description}
                  </p>
                )}

                <p className="text-sm font-bold text-gray-900 mt-1">
                  £{item.price?.toFixed(2)}
                </p>
              </div>

              {/* RIGHT + BUTTON */}
              <button
                disabled={!storeOpen}
                onClick={() => storeOpen && addToCart(item)}
                className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center text-xl font-semibold hover:bg-amber-700 transition-colors flex-shrink-0 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
          ))}
      </div>
    </div>
  </div>
)}

{/* Promotions */}
{activePromotions?.length > 0 && (
  <div className="mt-5 mb-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-900">
        Promotional Offers
      </h3>

      <span className="text-xs text-gray-400 font-medium">
        Max. Redeemable 1
      </span>
    </div>

    <div className="flex flex-col gap-2">

      {activePromotions.map((promo) => {

        const qualifies =
          cartSubtotal >= (promo.minSpend || 0);

        return (
          <button
            key={promo.id}
            type="button"
            disabled={!qualifies}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (!qualifies) return;

              setSelectedPromotion({
  ...promo
});
            }}
            className={`
              w-full
              rounded-2xl
              border
              p-4
              text-left
              transition-all
              duration-200
              ${
                qualifies
                  ? 'border-gray-200 bg-white'
                  : 'border-gray-200 bg-gray-50 opacity-70'
              }
            `}
          >
            <div className="flex items-start justify-between gap-3">

              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {promo.title}
                </p>

                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="bg-white border border-gray-200 rounded-lg px-2 py-1 font-semibold text-gray-700">
                    Minimum £{promo.minSpend?.toFixed(2)}
                  </span>

                  {qualifies ? (
                    <span className="text-green-600 font-bold">
                      Eligible
                    </span>
                  ) : (
                    <span className="text-gray-400 font-medium">
                      Add more to unlock
                    </span>
                  )}
                </div>
              </div>

              <div
                className={`
                  rounded-xl
                  px-3
                  py-2
                  text-xs
                  font-bold
                  whitespace-nowrap
                  ${
                    qualifies
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }
                `}
              >
                {qualifies ? 'Choose' : 'Locked'}
              </div>

            </div>
          </button>
        );
      })}

    </div>
  </div>
)}


              {!storeOpen && (
                <div className="bg-red-50 border border-red-100 p-3 rounded-xl mb-3">
                  <p className="text-red-700 text-sm font-medium text-center">
                    {ordersPaused ? 'We are not accepting orders currently, please check back soon.' : 'Store is closed'}
                  </p>
                </div>
              )}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-semibold">£{cartSubtotal.toFixed(2)}</span></div>
                {orderType === 'delivery' && <div className="flex justify-between"><span className="text-gray-600">Delivery</span><span className="font-semibold">£{deliveryFee.toFixed(2)}</span></div>}
                <div className="flex justify-between text-base font-bold border-t border-gray-200 pt-2"><span>Total</span><span>£{total.toFixed(2)}</span></div>
              </div>
              <button 
                onClick={() => { if (storeOpen) { setShowMobileCart(false); navigate('/checkout'); } }} 
                disabled={!storeOpen}
                className={`w-full py-3 rounded-xl font-semibold ${
                  storeOpen 
                    ? 'bg-gray-900 text-white hover:bg-amber-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {storeOpen ? 'Go to Checkout' : (ordersPaused ? 'Ordering paused' : 'Store Closed')}
              </button>
            </div>
          </div>
        </div>
      )}

{promotionInfoModal && (
  <div className="fixed inset-0 z-[125] bg-black/50 flex items-end">

    <div className="bg-white w-full rounded-t-3xl p-5 max-h-[80vh] overflow-y-auto">

      <div className="flex items-center justify-between mb-5">

        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {promotionInfoModal.title}
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Promotion details
          </p>
        </div>

        <button
          onClick={() => setPromotionInfoModal(null)}
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
        >
          ✕
        </button>

      </div>

      {promotionInfoModal.subtitle && (
        <div className="text-sm leading-5 text-gray-700 whitespace-pre-wrap">
          {promotionInfoModal.subtitle}
        </div>
      )}

    </div>

  </div>
)}


      {selectedPromotion && (
  <div className="fixed inset-0 z-[120] bg-black/50 flex items-end">
    <div className="bg-white w-full rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto">

      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Choose Your Free Item
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Included with your promotion
          </p>
        </div>

        <button
          onClick={() => setSelectedPromotion(null)}
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
        >
          ✕
        </button>
      </div>

      <div className="space-y-3">

        {selectedPromotion.rewardItems?.map((item, index) => (
  <button
    key={`${item.id}-${index}`}
    onClick={() => {
      if (!storeOpen) return;

      addToCart(
        {
          ...item,
          price: 0,
          promotionReward: true
        },
        1,
        {}
      );

      setSelectedPromotion(null);
    }}
    disabled={!storeOpen}
    className="
      w-full
      border
      border-gray-200
      rounded-2xl
      px-4
      py-4
      flex
      items-center
      gap-3
      hover:border-amber-500
      transition-colors
      text-left
    "
  >

    <div className="flex items-center gap-3 flex-1">

  {item.image && (
    <img
      src={item.image}
      alt=""
      className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
    />
  )}

  <div className="flex-1">
    <p className="font-semibold text-gray-900">
      {item.name}
    </p>

    <p className="text-sm text-green-600 font-bold mt-1">
      FREE
    </p>
  </div>

</div>

  </button>
))}
      </div>
    </div>
  </div>
)}

      {selectedItem && <ItemModal item={selectedItem} onClose={() => setSelectedItem(null)} storeOpen={storeOpen} ordersPaused={ordersPaused} />}
      <Footer restaurant={restaurant} />
    </div>
  );
}

function MenuItemCard({ item, onClick, rewardsEnabled, pointsPerPound }) {
  const points = Math.round(item.price * pointsPerPound);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-100 p-4 flex gap-4 cursor-pointer hover:border-amber-200 hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">
            {item.name}
          </h3>

          {item.popular && (
            <span className="flex-shrink-0 flex items-center gap-0.5 text-xs text-amber-600 font-medium">
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
              Popular
            </span>
          )}
        </div>

        {item.isNew && (
          <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-md mb-1.5 bg-green-100 text-green-700">
            NEW
          </span>
        )}

        {item.isVeggie && (
          <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-md mb-1.5 bg-emerald-100 text-emerald-700">
            VEGGIE
          </span>
        )}

        {item.isSpicy && (
          <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-md mb-1.5 bg-red-100 text-red-700">
            🔥 Hot
          </span>
        )}

        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-2">
          {item.description}
        </p>

        {/* PRICE + POINTS */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <span>£{item.price.toFixed(2)}</span>

          {rewardsEnabled && (
            <>
              <span className="text-gray-400">•</span>
              <span className="text-amber-600 font-semibold text-[12px]">
                {points} pts
              </span>
            </>
          )}
        </div>
      </div>

      {item.image ? (
        <div className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-gray-100">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <button className="absolute bottom-1.5 right-1.5 w-7 h-7 bg-gray-900 text-white rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors duration-200 shadow-md">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center">
          <button className="w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors duration-200 shadow-md">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function CartSidebar({ navigate, deliveryFee, total, storeOpen = true, ordersPaused = false }) {
  const { cart, cartCount, cartSubtotal, orderType, updateQuantity } = useApp();
  return (
    <div className="sticky top-16 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-gray-700" />
          <h3 className="font-bold text-gray-900">Your Order</h3>
          {cartCount > 0 && <span className="ml-auto text-sm text-gray-500">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>}
        </div>
      </div>

      {!storeOpen && cart.length > 0 && (
        <div className="bg-red-50 border-b border-red-100 p-3">
          <p className="text-red-700 text-sm font-medium text-center">
            {ordersPaused
              ? 'We are not accepting orders currently, please check back soon.'
              : 'Store is closed. You can view your basket but cannot checkout at this time.'}
          </p>
        </div>
      )}

      {cart.length === 0 ? (
        <div className="p-6 text-center">
          <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">Your basket is empty</p>
          <p className="text-gray-400 text-xs mt-1">Add items to get started</p>
        </div>
      ) : (
        <>
          <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
            {cart.map(item => (
              <div key={item.cartItemId} className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg">
                  <button onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)} className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900 text-lg font-medium">−</button>
                  <span className="w-5 text-center text-sm font-semibold text-gray-900">{item.quantity}</span>
                  <button disabled={!storeOpen} onClick={() => storeOpen && updateQuantity(item.cartItemId, item.quantity + 1)} className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900 text-lg font-medium disabled:text-gray-300 disabled:cursor-not-allowed">+</button>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-800 font-medium line-clamp-1 block">{item.name}</span>
                  {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                    <span className="text-xs text-gray-500 line-clamp-1 block">
                      {Object.entries(item.selectedOptions).map(([optId, choice]) => {
                        if (Array.isArray(choice)) {
                          return choice.map(c => c.name).join(', ');
                        }
                        return choice?.name || '';
                      }).filter(Boolean).join(' · ')}
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-gray-900">£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>£{cartSubtotal.toFixed(2)}</span></div>
            {orderType === 'delivery' && <div className="flex justify-between text-sm text-gray-600"><span>Delivery</span><span>£{deliveryFee.toFixed(2)}</span></div>}
            <div className="flex justify-between text-base font-bold text-gray-900 pt-1 border-t border-gray-100"><span>Total</span><span>£{total.toFixed(2)}</span></div>
          </div>
          <div className="p-4 pt-0">
            <button 
              onClick={() => storeOpen && navigate('/checkout')} 
              disabled={!storeOpen}
              className={`w-full py-3.5 rounded-xl font-semibold text-base transition-colors duration-200 ${
                storeOpen 
                  ? 'bg-gray-900 text-white hover:bg-amber-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {storeOpen ? `Go to checkout · £${total.toFixed(2)}` : (ordersPaused ? 'Ordering paused' : 'Store Closed')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
